import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Users, Calendar, Clock, Check, X, UserCog, MapPin, Trash2, MessageCircle, ArrowLeft, Mail, Shield, Download, Eye, EyeOff, Upload, Edit, Phone, BarChart3, Archive } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { getImageUrl } from '../utils/imageUrl';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminDashboard() {
  const [areas, setAreas] = useState(["All Areas"]);
  const [stats, setStats] = useState(null);
  const [pendingUsers, setPendingUsers] = useState([]);
  const [areaManagers, setAreaManagers] = useState([]);
  const [chapterSettings, setChapterSettings] = useState({});
  const [chapterAssistants, setChapterAssistants] = useState({});
  const [allAdmins, setAllAdmins] = useState([]);
  const [allMembers, setAllMembers] = useState([]);
  const [allEvents, setAllEvents] = useState([]);
  const [memberSearch, setMemberSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [activeView, setActiveView] = useState('dashboard'); // dashboard, members, pending, managers, admins, events, export
  const [selectedMember, setSelectedMember] = useState('');
  const [selectedArea, setSelectedArea] = useState('');
  const [selectedAdminMember, setSelectedAdminMember] = useState('');
  const [eventAreaFilter, setEventAreaFilter] = useState('All Areas');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isAdminDialogOpen, setIsAdminDialogOpen] = useState(false);
  const [exportStartDate, setExportStartDate] = useState('');
  const [exportEndDate, setExportEndDate] = useState('');
  const [isExporting, setIsExporting] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetPassword, setResetPassword] = useState('');
  const [isResettingPassword, setIsResettingPassword] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [isImporting, setIsImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [isFixingAreas, setIsFixingAreas] = useState(false);
  const [showEditMemberModal, setShowEditMemberModal] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [editMemberForm, setEditMemberForm] = useState({});
  const [savingMember, setSavingMember] = useState(false);
  const [showAddMember, setShowAddMember] = useState(false);
  const [addMemberForm, setAddMemberForm] = useState({ full_name: '', email: '', phone: '', area: '', business_name: '', business_category: '' });
  const [addingMember, setAddingMember] = useState(false);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, pendingRes, managersRes, adminsRes, membersRes, eventsRes, chaptersRes, areasRes] = await Promise.all([
        axios.get(`${API}/admin/stats`),
        axios.get(`${API}/admin/pending-users`),
        axios.get(`${API}/admin/area-managers`),
        axios.get(`${API}/admin/admins`),
        axios.get(`${API}/users`),
        axios.get(`${API}/events`),
        axios.get(`${API}/chapters/leaders`),
        axios.get(`${API}/areas`)
      ]);
      setStats(statsRes.data);
      setPendingUsers(pendingRes.data);
      setAreaManagers(managersRes.data);
      setAllAdmins(adminsRes.data);
      setChapterSettings(chaptersRes.data.chapter_settings || {});
      setChapterAssistants(chaptersRes.data.assistants || {});
      setAreas(["All Areas", ...areasRes.data]);
      // Sort members alphabetically
      const sortedMembers = membersRes.data.sort((a, b) => 
        (a.full_name || '').localeCompare(b.full_name || '')
      );
      setAllMembers(sortedMembers);
      setAllEvents(eventsRes.data);
    } catch (error) {
      toast.error('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (userId) => {
    try {
      await axios.post(`${API}/admin/approve-user/${userId}`);
      toast.success('User approved!');
      fetchData();
    } catch (error) {
      toast.error('Failed to approve user');
    }
  };

  const handleReject = async (userId) => {
    if (!window.confirm('Reject this application?')) return;
    try {
      await axios.post(`${API}/admin/reject-user/${userId}`);
      toast.success('User rejected');
      fetchData();
    } catch (error) {
      toast.error('Failed to reject user');
    }
  };

  const handleAddMember = async () => {
    if (!addMemberForm.full_name.trim() || !addMemberForm.email.trim()) {
      toast.error('Name and email are required');
      return;
    }
    setAddingMember(true);
    try {
      const res = await axios.post(`${API}/admin/add-member`, addMemberForm);
      toast.success(res.data.message);
      setShowAddMember(false);
      setAddMemberForm({ full_name: '', email: '', phone: '', area: '', business_name: '', business_category: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add member');
    } finally {
      setAddingMember(false);
    }
  };

  const handleArchiveEvent = async (eventId) => {
    if (!window.confirm('Archive this event? It will be hidden from the events page but attendee data is preserved.')) return;
    try {
      await axios.patch(`${API}/events/${eventId}`, { archived: true });
      toast.success('Event archived');
      fetchData();
    } catch (error) {
      toast.error('Failed to archive event');
    }
  };

  const handleAssignAreaManager = async () => {
    if (!selectedMember || !selectedArea) {
      toast.error('Please select both a member and an area');
      return;
    }
    try {
      await axios.post(`${API}/admin/assign-area-manager`, {
        user_id: selectedMember,
        area: selectedArea
      });
      toast.success('Area manager assigned!');
      setSelectedMember('');
      setSelectedArea('');
      setIsDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign area manager');
    }
  };

  const handleRemoveAreaManager = async (userId) => {
    if (!window.confirm('Remove this area manager?')) return;
    try {
      await axios.post(`${API}/admin/remove-area-manager/${userId}`);
      toast.success('Area manager removed');
      fetchData();
    } catch (error) {
      toast.error('Failed to remove area manager');
    }
  };

  const handleAssignAdmin = async () => {
    if (!selectedAdminMember) {
      toast.error('Please select a member');
      return;
    }
    try {
      await axios.post(`${API}/admin/assign-admin`, {
        user_id: selectedAdminMember
      });
      toast.success('Admin assigned!');
      setSelectedAdminMember('');
      setIsAdminDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign admin');
    }
  };

  const handleRemoveAdmin = async (userId, userName) => {
    if (!window.confirm(`Remove admin access from ${userName}?`)) return;
    try {
      await axios.post(`${API}/admin/remove-admin/${userId}`);
      toast.success('Admin access removed');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove admin');
    }
  };

  const handleDeleteMember = async (userId, memberName) => {
    if (!window.confirm(`Are you sure you want to delete ${memberName}? This will permanently remove their account and all their posts, events, and messages. This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/delete-member/${userId}`);
      toast.success('Member deleted successfully');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete member');
    }
  };

  const handleExportMembers = async () => {
    if (!exportStartDate || !exportEndDate) {
      toast.error('Please select both start and end dates');
      return;
    }
    if (exportStartDate > exportEndDate) {
      toast.error('Start date must be before end date');
      return;
    }
    
    setIsExporting(true);
    try {
      const response = await axios.get(`${API}/admin/export-members`, {
        params: { start_date: exportStartDate, end_date: exportEndDate },
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `mbn_members_${exportStartDate}_to_${exportEndDate}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Export downloaded successfully');
    } catch (error) {
      toast.error('Failed to export members');
    } finally {
      setIsExporting(false);
    }
  };

  const handleAdminResetPassword = async () => {
    if (!resetEmail || !resetPassword) {
      toast.error('Please enter both email and password');
      return;
    }
    if (resetPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setIsResettingPassword(true);
    try {
      await axios.post(`${API}/admin/reset-user-password`, {
        email: resetEmail,
        new_password: resetPassword
      });
      toast.success(`Password reset successfully for ${resetEmail}`);
      setResetEmail('');
      setResetPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setIsResettingPassword(false);
    }
  };

  const handleBulkImport = async () => {
    if (!importFile) {
      toast.error('Please select an Excel file');
      return;
    }
    
    setIsImporting(true);
    setImportResult(null);
    
    try {
      const formData = new FormData();
      formData.append('file', importFile);
      
      const response = await axios.post(`${API}/admin/bulk-import-members`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setImportResult(response.data);
      toast.success(`Successfully imported ${response.data.imported} members!`);
      setImportFile(null);
      fetchData(); // Refresh the member list
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to import members');
    } finally {
      setIsImporting(false);
    }
  };

  const handleFixBrackenfellAreas = async () => {
    if (!window.confirm("This will update all members with area 'Brackenfell' to 'Brackenfell and Surrounds'. Continue?")) return;
    
    setIsFixingAreas(true);
    try {
      const response = await axios.post(`${API}/admin/fix-area-names`);
      toast.success(response.data.message || 'Area names fixed successfully!');
      fetchData(); // Refresh stats
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to fix area names');
    } finally {
      setIsFixingAreas(false);
    }
  };

  const openEditMemberModal = (member) => {
    setEditingMember(member);
    setEditMemberForm({
      full_name: member.full_name || '',
      display_name: member.display_name || '',
      email: member.email || '',
      phone: member.phone || '',
      business_name: member.business_name || '',
      business_description: member.business_description || '',
      business_category: member.business_category || '',
      website: member.website || '',
      area: member.area || '',
      facebook: member.facebook || '',
      instagram: member.instagram || '',
      linkedin: member.linkedin || '',
      tiktok: member.tiktok || '',
      tags: member.tags || [],
      managed_areas: member.managed_areas || (member.managed_area ? [member.managed_area] : [])
    });
    setShowEditMemberModal(true);
  };

  const handleSaveMember = async (e) => {
    e.preventDefault();
    if (!editingMember) return;
    
    setSavingMember(true);
    try {
      await axios.patch(`${API}/admin/update-member/${editingMember.id}`, editMemberForm);
      toast.success('Member profile updated!');
      setShowEditMemberModal(false);
      setEditingMember(null);
      fetchData(); // Refresh the list
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update member');
    } finally {
      setSavingMember(false);
    }
  };

  if (loading) return <div className="text-center py-12">Loading...</div>;

  const availableMembers = allMembers;
  const availableForAdmin = allMembers.filter(m => !m.is_admin);
  const filteredEvents = eventAreaFilter === 'All Areas' 
    ? allEvents 
    : allEvents.filter(e => e.location?.toLowerCase().includes(eventAreaFilter.toLowerCase()));

  // Back button for sub-views
  const BackButton = () => (
    <Button
      variant="ghost"
      onClick={() => setActiveView('dashboard')}
      className="mb-4 text-[#5b7f95]"
    >
      <ArrowLeft className="w-4 h-4 mr-2" />
      Back to Dashboard
    </Button>
  );

  // Dashboard View with Clickable Stats
  if (activeView === 'dashboard') {
    return (
      <div className="max-w-7xl mx-auto px-6" data-testid="admin-dashboard">
        <h1 className="text-4xl font-bold text-[#2d3748] mb-8">Admin Dashboard</h1>

        {/* Reports Link */}
        <div className="mb-6">
          <Link to="/reports">
            <Button className="bg-[#5b7f95] hover:bg-[#4a6b80] text-white rounded-full" data-testid="reports-link">
              <BarChart3 className="w-4 h-4 mr-2" />
              Reports & Analytics
            </Button>
          </Link>
        </div>

        {/* Clickable Stats Grid */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <Card 
              className="border-gray-100 rounded-2xl shadow-sm cursor-pointer hover:shadow-md hover:-translate-y-1 transition-all"
              onClick={() => setActiveView('members')}
              data-testid="stat-total-members"
            >
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-[#64748b] mb-1">Total Members</p>
                    <p className="text-3xl font-bold text-[#2d3748]">{stats.total_users}</p>
                  </div>
                  <div className="w-12 h-12 bg-[#5b7f95]/10 rounded-full flex items-center justify-center">
                    <Users className="w-6 h-6 text-[#5b7f95]" />
                  </div>
                </div>
                <p className="text-xs text-[#5b7f95] mt-2">Click to view all members</p>
              </CardContent>
            </Card>

            <Card 
              className="border-gray-100 rounded-2xl shadow-sm cursor-pointer hover:shadow-md hover:-translate-y-1 transition-all"
              onClick={() => setActiveView('pending')}
              data-testid="stat-pending"
            >
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-[#64748b] mb-1">Pending Approval</p>
                    <p className="text-3xl font-bold text-[#cb9944]">{stats.pending_users}</p>
                  </div>
                  <div className="w-12 h-12 bg-[#cb9944]/10 rounded-full flex items-center justify-center">
                    <Clock className="w-6 h-6 text-[#cb9944]" />
                  </div>
                </div>
                <p className="text-xs text-[#cb9944] mt-2">Click to view pending</p>
              </CardContent>
            </Card>

            <Card 
              className="border-gray-100 rounded-2xl shadow-sm cursor-pointer hover:shadow-md hover:-translate-y-1 transition-all"
              onClick={() => setActiveView('managers')}
              data-testid="stat-managers"
            >
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-[#64748b] mb-1">Chapter Leaders</p>
                    <p className="text-3xl font-bold text-[#5b7f95]">{areaManagers.length}</p>
                  </div>
                  <div className="w-12 h-12 bg-[#5b7f95]/10 rounded-full flex items-center justify-center">
                    <UserCog className="w-6 h-6 text-[#5b7f95]" />
                  </div>
                </div>
                <p className="text-xs text-[#5b7f95] mt-2">Click to manage</p>
              </CardContent>
            </Card>

            <Card 
              className="border-gray-100 rounded-2xl shadow-sm cursor-pointer hover:shadow-md hover:-translate-y-1 transition-all"
              onClick={() => setActiveView('admins')}
              data-testid="stat-admins"
            >
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-[#64748b] mb-1">Admins</p>
                    <p className="text-3xl font-bold text-[#cb9944]">{allAdmins.length}</p>
                  </div>
                  <div className="w-12 h-12 bg-[#cb9944]/10 rounded-full flex items-center justify-center">
                    <Shield className="w-6 h-6 text-[#cb9944]" />
                  </div>
                </div>
                <p className="text-xs text-[#cb9944] mt-2">Click to manage</p>
              </CardContent>
            </Card>

            <Card 
              className="border-gray-100 rounded-2xl shadow-sm cursor-pointer hover:shadow-md hover:-translate-y-1 transition-all"
              onClick={() => setActiveView('events')}
              data-testid="stat-events"
            >
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-[#64748b] mb-1">Total Events</p>
                    <p className="text-3xl font-bold text-[#2d3748]">{stats.total_events}</p>
                  </div>
                  <div className="w-12 h-12 bg-[#5b7f95]/10 rounded-full flex items-center justify-center">
                    <Calendar className="w-6 h-6 text-[#5b7f95]" />
                  </div>
                </div>
                <p className="text-xs text-[#5b7f95] mt-2">Click to view events</p>
              </CardContent>
            </Card>

            <Card 
              className="border-gray-100 rounded-2xl shadow-sm cursor-pointer hover:shadow-md hover:-translate-y-1 transition-all bg-gradient-to-br from-[#5b7f95]/5 to-[#cb9944]/5"
              onClick={() => setActiveView('export')}
              data-testid="stat-export"
            >
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-[#64748b] mb-1">Admin Tools</p>
                    <p className="text-lg font-bold text-[#2d3748]">Export & Password Reset</p>
                  </div>
                  <div className="w-12 h-12 bg-[#5b7f95]/10 rounded-full flex items-center justify-center">
                    <Download className="w-6 h-6 text-[#5b7f95]" />
                  </div>
                </div>
                <p className="text-xs text-[#5b7f95] mt-2">Export by date range</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Areas Overview */}
        {stats && (
          <Card className="border-gray-100 rounded-2xl shadow-sm">
            <CardHeader>
              <CardTitle>Members by Area</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {stats.area_breakdown.map((area) => {
                  const manager = areaManagers.find(m => m.managed_area === area.area);
                  return (
                    <div key={area.area} className="p-4 bg-gray-50 rounded-xl">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-[#2d3748]">{area.area}</span>
                        <span className="text-lg font-bold text-[#5b7f95]">{area.count}</span>
                      </div>
                      {manager ? (
                        <div className="text-xs text-[#64748b] flex items-center gap-1">
                          <UserCog className="w-3 h-3 text-[#cb9944]" />
                          Leader: {manager.display_name || manager.full_name}
                        </div>
                      ) : (
                        <div className="text-xs text-[#64748b]">No chapter leader assigned</div>
                      )}
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    );
  }

  // All Members View
  if (activeView === 'members') {
    const filteredMembers = allMembers.filter(m => {
      if (!memberSearch) return true;
      const q = memberSearch.toLowerCase();
      return (m.full_name || '').toLowerCase().includes(q) ||
        (m.display_name || '').toLowerCase().includes(q) ||
        (m.business_name || '').toLowerCase().includes(q) ||
        (m.email || '').toLowerCase().includes(q) ||
        (m.phone || '').toLowerCase().includes(q) ||
        (m.area || '').toLowerCase().includes(q);
    });

    return (
      <div className="max-w-7xl mx-auto px-6" data-testid="members-list">
        <BackButton />
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-4xl font-bold text-[#2d3748]">All Members ({allMembers.length})</h1>
          <Dialog open={showAddMember} onOpenChange={setShowAddMember}>
            <DialogTrigger asChild>
              <Button className="bg-[#cb9944] hover:bg-[#b08435] text-white rounded-full" data-testid="add-member-btn">
                <Users className="w-4 h-4 mr-2" /> Add Member
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-white">
              <DialogHeader>
                <DialogTitle>Add New Member</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 pt-4">
                <div>
                  <Label>Full Name *</Label>
                  <Input value={addMemberForm.full_name} onChange={(e) => setAddMemberForm({...addMemberForm, full_name: e.target.value})} className="bg-gray-50 border-gray-200 rounded-xl" />
                </div>
                <div>
                  <Label>Email *</Label>
                  <Input type="email" value={addMemberForm.email} onChange={(e) => setAddMemberForm({...addMemberForm, email: e.target.value})} className="bg-gray-50 border-gray-200 rounded-xl" />
                </div>
                <div>
                  <Label>Phone</Label>
                  <Input value={addMemberForm.phone} onChange={(e) => setAddMemberForm({...addMemberForm, phone: e.target.value})} className="bg-gray-50 border-gray-200 rounded-xl" />
                </div>
                <div>
                  <Label>Preferred Home Chapter</Label>
                  <Select value={addMemberForm.area} onValueChange={(val) => setAddMemberForm({...addMemberForm, area: val})}>
                    <SelectTrigger className="bg-gray-50 border-gray-200 rounded-xl">
                      <SelectValue placeholder="Select chapter" />
                    </SelectTrigger>
                    <SelectContent>
                      {areas.filter(a => a !== 'All Areas').map(a => (
                        <SelectItem key={a} value={a}>{a}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Business Name</Label>
                  <Input value={addMemberForm.business_name} onChange={(e) => setAddMemberForm({...addMemberForm, business_name: e.target.value})} className="bg-gray-50 border-gray-200 rounded-xl" />
                </div>
                <p className="text-xs text-[#64748b]">Default password: MBN2026 (member will be prompted to change on first login)</p>
                <Button onClick={handleAddMember} disabled={addingMember || !addMemberForm.full_name.trim() || !addMemberForm.email.trim()} className="w-full bg-[#5b7f95] hover:bg-[#4a6b80] text-white rounded-full" data-testid="confirm-add-member-btn">
                  {addingMember ? 'Adding...' : 'Add Member'}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Search */}
        <div className="mb-6">
          <Input
            value={memberSearch}
            onChange={(e) => setMemberSearch(e.target.value)}
            placeholder="Search by name, business, email, phone, or chapter..."
            className="bg-gray-50 border-gray-200 rounded-xl max-w-md"
            data-testid="admin-member-search"
          />
          {memberSearch && (
            <p className="text-sm text-[#64748b] mt-2">Showing {filteredMembers.length} of {allMembers.length} members</p>
          )}
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredMembers.map((member) => (
            <Card key={member.id} className="border-gray-100 rounded-2xl shadow-sm">
              <CardContent className="pt-6">
                <div className="flex items-start gap-4">
                  <Avatar className="h-12 w-12">
                    <AvatarImage src={member.profile_image ? getImageUrl(member.profile_image) : undefined} />
                    <AvatarFallback className="bg-[#5b7f95] text-white">
                      {(member.display_name || member.full_name).charAt(0)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-[#2d3748] truncate">
                      {member.display_name || member.full_name}
                    </h3>
                    {member.business_name && (
                      <p className="text-sm text-[#5b7f95] truncate">{member.business_name}</p>
                    )}
                    <p className="text-xs text-[#64748b] flex items-center gap-1 mt-1">
                      <MapPin className="w-3 h-3" /> {member.area}
                    </p>
                    <p className="text-xs text-[#64748b] flex items-center gap-1 mt-0.5">
                      <Phone className="w-3 h-3" />
                      {member.phone ? (
                        <a href={`tel:${member.phone}`} className="hover:text-[#5b7f95]">{member.phone}</a>
                      ) : (
                        <span className="text-gray-400 italic">No phone</span>
                      )}
                    </p>
                    <p className="text-xs text-[#64748b] flex items-center gap-1 mt-0.5">
                      <Mail className="w-3 h-3" /> <span className="truncate">{member.email}</span>
                    </p>
                    <div className="flex items-center gap-2 mt-2">
                      {member.is_admin && (
                        <span className="px-2 py-0.5 bg-[#cb9944] text-white text-xs rounded-full">Admin</span>
                      )}
                      {member.is_area_manager && (
                        <span className="px-2 py-0.5 bg-[#5b7f95] text-white text-xs rounded-full">Chapter Leader</span>
                      )}
                      <div className="ml-auto flex gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditMemberModal(member)}
                          className="text-[#5b7f95] hover:text-[#4a6b80] hover:bg-[#5b7f95]/10 h-8 w-8 p-0"
                          data-testid="edit-member-btn"
                          title="Edit member"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        {!member.is_admin && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteMember(member.id, member.display_name || member.full_name)}
                            className="text-red-500 hover:text-red-700 hover:bg-red-50 h-8 w-8 p-0"
                            data-testid="delete-member-btn"
                            title="Delete member"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
        
        {/* Edit Member Modal */}
        <EditMemberModal 
          open={showEditMemberModal}
          onOpenChange={setShowEditMemberModal}
          member={editingMember}
          form={editMemberForm}
          setForm={setEditMemberForm}
          onSave={handleSaveMember}
          saving={savingMember}
          areas={areas.filter(a => a !== 'All Areas')}
        />
      </div>
    );
  }

  // Pending Users View
  if (activeView === 'pending') {
    return (
      <div className="max-w-7xl mx-auto px-6" data-testid="pending-list">
        <BackButton />
        <h1 className="text-4xl font-bold text-[#2d3748] mb-8">Pending Applications ({pendingUsers.length})</h1>
        
        {pendingUsers.length > 0 ? (
          <div className="space-y-4">
            {pendingUsers.map((user) => (
              <Card key={user.id} className="border-gray-100 rounded-2xl shadow-sm">
                <CardContent className="pt-6">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-4 flex-1">
                      <Avatar className="h-12 w-12">
                        <AvatarFallback className="bg-[#5b7f95] text-white">
                          {user.full_name.charAt(0)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1">
                        <h3 className="font-semibold text-[#2d3748]">{user.full_name}</h3>
                        <p className="text-sm text-[#64748b]">{user.email}</p>
                        {user.phone && (
                          <p className="text-sm text-[#64748b] flex items-center gap-1 mt-0.5">
                            <Phone className="w-3 h-3" /> {user.phone}
                          </p>
                        )}
                        <p className="text-sm text-[#5b7f95] mt-1 flex items-center gap-1">
                          <MapPin className="w-3 h-3" /> {user.area}
                        </p>
                        {user.business_name && (
                          <div className="mt-2">
                            <p className="text-sm font-semibold text-[#2d3748]">{user.business_name}</p>
                            {user.business_description && (
                              <p className="text-sm text-[#64748b] line-clamp-2">{user.business_description}</p>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-2 ml-4">
                      <Button
                        onClick={() => handleApprove(user.id)}
                        size="sm"
                        className="bg-green-500 hover:bg-green-600 text-white rounded-full"
                      >
                        <Check className="w-4 h-4 mr-1" /> Approve
                      </Button>
                      <Button
                        onClick={() => handleReject(user.id)}
                        size="sm"
                        variant="destructive"
                        className="rounded-full"
                      >
                        <X className="w-4 h-4 mr-1" /> Reject
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <div className="text-center py-12 text-[#64748b]">
            <p>No pending applications</p>
          </div>
        )}
      </div>
    );
  }

  // Area Managers View - Chapter by Chapter
  if (activeView === 'managers') {
    const CHAPTER_LIST = areas.filter(a => a !== 'All Areas');
    
    const getLeaderForChapter = (chapter) => {
      return areaManagers.find(m => 
        (m.managed_areas || []).includes(chapter) || m.managed_area === chapter
      );
    };

    const handleAssignAssistant = async (chapter, userId) => {
      try {
        await axios.put(`${API}/chapters/${encodeURIComponent(chapter)}/settings`, {
          assistant_leader_id: userId || ""
        });
        toast.success(`Assistant updated for ${chapter}`);
        fetchData();
      } catch (error) {
        toast.error('Failed to update assistant');
      }
    };

    const handleAssignLeader = async (chapter, userId) => {
      try {
        await axios.put(`${API}/chapters/${encodeURIComponent(chapter)}/leader`, {
          leader_id: userId || ""
        });
        toast.success(`Leader updated for ${chapter}`);
        fetchData();
      } catch (error) {
        toast.error('Failed to update leader');
      }
    };

    return (
      <div className="max-w-7xl mx-auto px-6" data-testid="managers-list">
        <BackButton />
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-4xl font-bold text-[#2d3748]">Chapter Management</h1>
        </div>

        <div className="space-y-4">
          {CHAPTER_LIST.map((chapter) => {
            const leader = getLeaderForChapter(chapter);
            const memberCount = allMembers.filter(m => m.area === chapter).length;
            
            return (
              <Card key={chapter} className="border-gray-100 rounded-2xl shadow-sm">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="text-lg font-bold text-[#2d3748]">{chapter}</h3>
                      <p className="text-sm text-[#64748b]">{memberCount} members</p>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Chapter Leader */}
                    <div className="p-4 bg-[#f0f5f8] rounded-xl">
                      <p className="text-xs font-semibold text-[#5b7f95] uppercase mb-2">Chapter Leader</p>
                      {leader ? (
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <Avatar className="h-10 w-10">
                              <AvatarImage src={leader.profile_image ? getImageUrl(leader.profile_image) : undefined} />
                              <AvatarFallback className="bg-[#cb9944] text-white text-sm">
                                {(leader.display_name || leader.full_name).charAt(0)}
                              </AvatarFallback>
                            </Avatar>
                            <div>
                              <p className="font-medium text-sm text-[#2d3748]">{leader.display_name || leader.full_name}</p>
                              <p className="text-xs text-[#64748b]">{leader.email}</p>
                            </div>
                          </div>
                          <Button
                            onClick={() => handleAssignLeader(chapter, '')}
                            size="sm"
                            variant="ghost"
                            className="text-red-400 hover:text-red-600 hover:bg-red-50 rounded-full h-8 w-8 p-0"
                            title="Remove leader"
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      ) : (
                        <Select 
                          value=""
                          onValueChange={(val) => handleAssignLeader(chapter, val)}
                        >
                          <SelectTrigger className="h-auto min-h-[40px] bg-white" data-testid={`leader-select-${chapter}`}>
                            <SelectValue placeholder="Select leader..." />
                          </SelectTrigger>
                          <SelectContent>
                            {availableMembers.map((m) => (
                              <SelectItem key={m.id} value={m.id}>
                                {m.display_name || m.full_name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    </div>

                    {/* Assistant Chapter Leader */}
                    <div className="p-4 bg-[#faf7f0] rounded-xl">
                      <p className="text-xs font-semibold text-[#cb9944] uppercase mb-2">Assistant Leader</p>
                      {(() => {
                        const settings = chapterSettings[chapter] || {};
                        const assistantId = settings.assistant_leader_id || '';
                        const assistant = assistantId ? chapterAssistants[assistantId] : null;
                        
                        return assistant ? (
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <Avatar className="h-10 w-10">
                                <AvatarImage src={assistant.profile_image ? getImageUrl(assistant.profile_image) : undefined} />
                                <AvatarFallback className="bg-[#cb9944]/70 text-white text-sm">
                                  {(assistant.display_name || assistant.full_name).charAt(0)}
                                </AvatarFallback>
                              </Avatar>
                              <div>
                                <p className="font-medium text-sm text-[#2d3748]">{assistant.display_name || assistant.full_name}</p>
                                <p className="text-xs text-[#64748b]">{assistant.email}</p>
                              </div>
                            </div>
                            <Button
                              onClick={() => handleAssignAssistant(chapter, '')}
                              size="sm"
                              variant="ghost"
                              className="text-red-400 hover:text-red-600 hover:bg-red-50 rounded-full h-8 w-8 p-0"
                              title="Remove assistant"
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                        ) : (
                          <Select 
                            value=""
                            onValueChange={(val) => handleAssignAssistant(chapter, val)}
                          >
                            <SelectTrigger className="h-auto min-h-[40px] bg-white" data-testid={`assistant-select-${chapter}`}>
                              <SelectValue placeholder="Select assistant..." />
                            </SelectTrigger>
                            <SelectContent>
                              {availableMembers.map((m) => (
                                <SelectItem key={m.id} value={m.id}>
                                  {m.display_name || m.full_name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        );
                      })()}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    );
  }

  // Admins View
  if (activeView === 'admins') {
    return (
      <div className="max-w-7xl mx-auto px-6" data-testid="admins-list">
        <BackButton />
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-4xl font-bold text-[#2d3748]">Admins ({allAdmins.length})</h1>
          <Dialog open={isAdminDialogOpen} onOpenChange={setIsAdminDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-[#cb9944] hover:bg-[#b08435] text-white rounded-full">
                <Shield className="w-4 h-4 mr-2" />
                Assign Admin
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Assign Admin Access</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div>
                  <label className="text-sm font-medium text-gray-700">Select Member</label>
                  <Select value={selectedAdminMember} onValueChange={setSelectedAdminMember}>
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Choose a member" />
                    </SelectTrigger>
                    <SelectContent>
                      {availableForAdmin.map((member) => (
                        <SelectItem key={member.id} value={member.id}>
                          {member.display_name || member.full_name} - {member.email}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button onClick={handleAssignAdmin} className="w-full bg-[#cb9944] hover:bg-[#b08435]">
                  Assign as Admin
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {allAdmins.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {allAdmins.map((admin) => (
              <Card key={admin.id} className="border-gray-100 rounded-2xl shadow-sm border-l-4 border-l-[#cb9944]">
                <CardContent className="pt-6">
                  <div className="flex items-start gap-4">
                    <Avatar className="h-12 w-12">
                      <AvatarImage src={admin.profile_image ? getImageUrl(admin.profile_image) : undefined} />
                      <AvatarFallback className="bg-[#cb9944] text-white">
                        {(admin.display_name || admin.full_name).charAt(0)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1">
                      <h3 className="font-semibold text-[#2d3748]">{admin.display_name || admin.full_name}</h3>
                      <p className="text-sm text-[#5b7f95]">{admin.business_name || 'No business'}</p>
                      <p className="text-xs text-[#64748b] flex items-center gap-1 mt-1">
                        <Mail className="w-3 h-3" /> {admin.email}
                      </p>
                      <div className="flex items-center gap-2 mt-3">
                        <span className="px-2 py-0.5 bg-[#cb9944] text-white text-xs rounded-full">Admin</span>
                        {allAdmins.length > 1 && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRemoveAdmin(admin.id, admin.display_name || admin.full_name)}
                            className="ml-auto text-red-500 hover:text-red-700 hover:bg-red-50 h-8 px-2"
                          >
                            <X className="w-4 h-4 mr-1" />
                            Remove
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <div className="text-center py-12 text-[#64748b]">
            <p>No admins found</p>
          </div>
        )}
      </div>
    );
  }

  // Events View
  if (activeView === 'events') {
    return (
      <div className="max-w-7xl mx-auto px-6" data-testid="events-list">
        <BackButton />
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-4xl font-bold text-[#2d3748]">All Events ({filteredEvents.length})</h1>
          <Select value={eventAreaFilter} onValueChange={setEventAreaFilter}>
            <SelectTrigger className="w-64">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {areas.map((area) => (
                <SelectItem key={area} value={area}>{area}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {filteredEvents.length > 0 ? (
          <div className="space-y-4">
            {filteredEvents.map((event) => (
              <Link key={event.id} to={`/events/${event.id}`}>
                <Card className="border-gray-100 rounded-2xl shadow-sm hover:shadow-md transition-shadow mb-4">
                  <CardContent className="pt-6">
                    <div className="flex items-start gap-4">
                      {event.image && (
                        <div className="w-24 h-24 rounded-xl overflow-hidden flex-shrink-0">
                          <img src={getImageUrl(event.image)} alt={event.title} className="w-full h-full object-cover" />
                        </div>
                      )}
                      <div className="flex-1">
                        <h3 className="font-semibold text-lg text-[#2d3748]">{event.title}</h3>
                        <p className="text-sm text-[#64748b]">by {event.user_name}</p>
                        <div className="flex flex-wrap gap-4 mt-2 text-sm text-[#64748b]">
                          <span className="flex items-center gap-1">
                            <Calendar className="w-4 h-4 text-[#5b7f95]" />
                            {format(new Date(event.date), 'PPP p')}
                          </span>
                          <span className="flex items-center gap-1">
                            <MapPin className="w-4 h-4 text-[#5b7f95]" />
                            {event.location}
                          </span>
                          <span className="flex items-center gap-1">
                            <Users className="w-4 h-4 text-[#5b7f95]" />
                            {event.attendees.length} attending
                          </span>
                          {event.is_paid && event.price && (
                            <span className="font-semibold text-[#cb9944]">R{event.price.toFixed(2)}</span>
                          )}
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleArchiveEvent(event.id); }}
                        className="text-[#64748b] hover:text-[#cb9944] hover:bg-[#cb9944]/10 rounded-full flex-shrink-0"
                        title="Archive event"
                        data-testid="archive-event-btn"
                      >
                        <Archive className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        ) : (
          <div className="text-center py-12 text-[#64748b]">
            <p>No events found</p>
          </div>
        )}
      </div>
    );
  }

  // Export Members View
  if (activeView === 'export') {
    return (
      <div className="max-w-2xl mx-auto px-6" data-testid="export-members">
        <BackButton />
        <h1 className="text-4xl font-bold text-[#2d3748] mb-8">Admin Tools</h1>
        
        {/* Password Reset Card */}
        <Card className="border-gray-100 rounded-2xl shadow-sm mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-[#cb9944]" />
              Reset User Password
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-[#64748b] mb-6">
              Directly reset a user's password without email verification. Use this if the forgot password email isn't working.
            </p>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-[#2d3748] mb-2">User Email</label>
                <input
                  type="email"
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  placeholder="user@example.com"
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#5b7f95]"
                  data-testid="reset-email-input"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-[#2d3748] mb-2">New Password</label>
                <div className="relative">
                  <input
                    type={showResetPassword ? "text" : "password"}
                    value={resetPassword}
                    onChange={(e) => setResetPassword(e.target.value)}
                    placeholder="At least 6 characters"
                    className="w-full px-4 py-2 pr-12 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#5b7f95]"
                    data-testid="reset-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowResetPassword(!showResetPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showResetPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>
              
              <Button 
                onClick={handleAdminResetPassword}
                disabled={isResettingPassword || !resetEmail || !resetPassword}
                className="w-full bg-[#cb9944] hover:bg-[#b8863c] text-white rounded-full py-6"
                data-testid="admin-reset-btn"
              >
                {isResettingPassword ? 'Resetting...' : 'Reset Password'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Full Database Backup Card */}
        <Card className="border-gray-100 rounded-2xl shadow-sm mb-6 border-2 border-green-500/20">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Download className="w-5 h-5 text-green-600" />
              Download Full Backup
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-[#64748b] mb-4">
              Download a complete backup of the entire database as a JSON file. Includes:
            </p>
            <ul className="text-sm text-[#64748b] mb-6 space-y-1">
              <li>All members (profiles, businesses, tags)</li>
              <li>All community posts and comments</li>
              <li>All direct messages and group chat messages</li>
              <li>All events and attendee lists</li>
              <li>Payment records and chapter settings</li>
            </ul>
            
            <Button 
              onClick={async () => {
                try {
                  toast.info('Preparing backup...');
                  const response = await axios.get(`${API}/admin/full-backup`, { responseType: 'blob' });
                  const url = window.URL.createObjectURL(new Blob([response.data]));
                  const link = document.createElement('a');
                  link.href = url;
                  const date = new Date().toISOString().slice(0,10);
                  link.setAttribute('download', `mbn_full_backup_${date}.json`);
                  document.body.appendChild(link);
                  link.click();
                  link.remove();
                  window.URL.revokeObjectURL(url);
                  toast.success('Backup downloaded!');
                } catch (error) {
                  toast.error('Failed to download backup');
                }
              }}
              className="w-full bg-green-600 hover:bg-green-700 text-white rounded-full py-6"
              data-testid="download-backup-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Download Full Database Backup
            </Button>
          </CardContent>
        </Card>

        {/* Fix Area Names Card */}
        <Card className="border-gray-100 rounded-2xl shadow-sm mb-6 border-2 border-[#cb9944]/20">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MapPin className="w-5 h-5 text-[#cb9944]" />
              Fix Member Area Names
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-[#64748b] mb-4">
              Fix area names that don't match the standardized chapter names. This will update:
            </p>
            <ul className="text-sm text-[#64748b] mb-6 space-y-1">
              <li>• <strong>Brackenfell</strong> → Brackenfell and Surrounds</li>
              <li>• <strong>Strand</strong> → Helderberg</li>
              <li>• <strong>Boland Winelands</strong> → Boland</li>
              <li>• <strong>South Peninsula</strong> → Southern Peninsula</li>
              <li>• <strong>Blouberg</strong> → West Coast Neighbours</li>
              <li>• <strong>Rondebosch</strong> → Rondebosch and Surrounds</li>
            </ul>
            
            <Button 
              onClick={handleFixBrackenfellAreas}
              disabled={isFixingAreas}
              className="w-full bg-[#cb9944] hover:bg-[#b8863c] text-white rounded-full py-6"
              data-testid="fix-areas-btn"
            >
              {isFixingAreas ? 'Fixing...' : 'Fix Area Names Now'}
            </Button>
          </CardContent>
        </Card>

        {/* Bulk Import Card */}
        <Card className="border-gray-100 rounded-2xl shadow-sm mb-6 border-2 border-[#5b7f95]/20">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5 text-[#5b7f95]" />
              Bulk Import Members
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-[#64748b] mb-4">
              Import existing members from an Excel file. All imported members will have:
            </p>
            <ul className="text-sm text-[#64748b] mb-6 space-y-1">
              <li>• Default password: <strong>MBN2026</strong></li>
              <li>• Auto-approved status</li>
              <li>• Prompted to update their profile on first login</li>
            </ul>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-[#2d3748] mb-2">Excel File (.xlsx)</label>
                <input
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={(e) => setImportFile(e.target.files[0])}
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#5b7f95] file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-[#5b7f95] file:text-white hover:file:bg-[#4a6b80]"
                  data-testid="import-file-input"
                />
              </div>
              
              <Button 
                onClick={handleBulkImport}
                disabled={isImporting || !importFile}
                className="w-full bg-[#5b7f95] hover:bg-[#4a6b80] text-white rounded-full py-6"
                data-testid="import-btn"
              >
                {isImporting ? 'Importing...' : 'Import Members'}
              </Button>
              
              {importResult && (
                <div className={`p-4 rounded-lg ${importResult.imported > 0 ? 'bg-green-50 border border-green-200' : 'bg-yellow-50 border border-yellow-200'}`}>
                  <p className="font-medium text-[#2d3748]">Import Results:</p>
                  <p className="text-sm">✅ Imported: {importResult.imported}</p>
                  <p className="text-sm">⏭️ Skipped: {importResult.skipped}</p>
                  {importResult.errors && importResult.errors.length > 0 && (
                    <div className="mt-2">
                      <p className="text-sm text-red-600">Errors:</p>
                      <ul className="text-xs text-red-500">
                        {importResult.errors.map((err, i) => (
                          <li key={`err-${i}`}>{err}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>
            
            <div className="mt-6 p-4 bg-gray-50 rounded-lg">
              <p className="text-sm font-medium text-[#2d3748] mb-2">Required Excel columns:</p>
              <p className="text-xs text-[#64748b]">
                <strong>Email</strong> (required), <strong>Full Name</strong> (required), Phone, Area, Business Name, Business Category, Business Description, Website, Facebook, Instagram, LinkedIn, TikTok
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Export Card */}
        <Card className="border-gray-100 rounded-2xl shadow-sm">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Download className="w-5 h-5 text-[#5b7f95]" />
              Export Members to Excel
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-[#64748b] mb-6">
              Select a date range to export approved members who joined during that period. 
              This is useful for syncing new members with the website.
            </p>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-[#2d3748] mb-2">Start Date</label>
                <input
                  type="date"
                  value={exportStartDate}
                  onChange={(e) => setExportStartDate(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#5b7f95]"
                  data-testid="export-start-date"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-[#2d3748] mb-2">End Date</label>
                <input
                  type="date"
                  value={exportEndDate}
                  onChange={(e) => setExportEndDate(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#5b7f95]"
                  data-testid="export-end-date"
                />
              </div>
              
              <Button 
                onClick={handleExportMembers}
                disabled={isExporting || !exportStartDate || !exportEndDate}
                className="w-full bg-[#5b7f95] hover:bg-[#4a6b80] text-white rounded-full py-6"
                data-testid="export-btn"
              >
                {isExporting ? (
                  'Exporting...'
                ) : (
                  <>
                    <Download className="w-4 h-4 mr-2" />
                    Download Excel File
                  </>
                )}
              </Button>
            </div>
            
            <div className="mt-6 p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-[#64748b]">
                <strong>Exported fields:</strong> Full Name, Email, Phone, Area/Chapter, 
                Business Name, Business Category, Business Description, Website, Social Media, Join Date
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return null;
}

// Edit Member Modal Component - rendered outside the conditional views
export function EditMemberModal({ open, onOpenChange, member, form, setForm, onSave, saving, areas = [] }) {
  const [uploadingProfile, setUploadingProfile] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  
  if (!member) return null;

  const handleProfileImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || !file.type.startsWith('image/')) return;
    setUploadingProfile(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/upload/member-image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      await axios.patch(`${API}/admin/update-member/${member.id}`, {
        profile_image: res.data.image_url
      });
      setForm({ ...form, profile_image: res.data.image_url });
      toast.success('Profile photo updated!');
    } catch (err) {
      toast.error('Failed to upload profile photo');
    } finally {
      setUploadingProfile(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || !file.type.startsWith('image/')) return;
    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/upload/member-image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      await axios.patch(`${API}/admin/update-member/${member.id}`, {
        business_logo: res.data.image_url
      });
      setForm({ ...form, business_logo: res.data.image_url });
      toast.success('Business logo updated!');
    } catch (err) {
      toast.error('Failed to upload logo');
    } finally {
      setUploadingLogo(false);
    }
  };
  
  const CATEGORIES = [
    "Women's Lifestyle",
    "Health and Wellness",
    "Food and Beverage",
    "Arts & Crafts",
    "Professional Services",
    "Beauty & Fashion",
    "Education & Training",
    "Home & Living",
    "Events & Entertainment",
    "Technology",
    "Other"
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-[#2d3748]">Edit Member Profile</DialogTitle>
        </DialogHeader>
        <form onSubmit={onSave} className="space-y-4 mt-4">
          {/* Profile Photo & Business Logo */}
          <div className="grid grid-cols-2 gap-6 pb-4 border-b border-gray-100">
            <div className="text-center">
              <Label className="mb-2 block">Profile Photo</Label>
              <div className="relative mx-auto w-24 h-24 rounded-full overflow-hidden bg-gray-100 mb-2">
                {(form.profile_image || member.profile_image) ? (
                  <img src={getImageUrl(form.profile_image || member.profile_image)} alt="Profile" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-[#5b7f95] text-white text-2xl font-bold">
                    {(form.full_name || member.full_name || '?').charAt(0)}
                  </div>
                )}
              </div>
              <label className="cursor-pointer">
                <div className="inline-flex items-center gap-1 px-3 py-1.5 bg-[#5b7f95] text-white text-xs rounded-full hover:bg-[#4a6b80] transition-colors">
                  <Upload className="w-3 h-3" />
                  {uploadingProfile ? 'Uploading...' : 'Upload Photo'}
                </div>
                <input type="file" accept="image/*" onChange={handleProfileImageUpload} className="hidden" disabled={uploadingProfile} />
              </label>
            </div>
            <div className="text-center">
              <Label className="mb-2 block">Business Logo</Label>
              <div className="relative mx-auto w-24 h-24 rounded-xl overflow-hidden bg-gray-100 mb-2">
                {(form.business_logo || member.business_logo) ? (
                  <img src={getImageUrl(form.business_logo || member.business_logo)} alt="Logo" className="w-full h-full object-contain" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-gray-400 text-xs">No logo</div>
                )}
              </div>
              <label className="cursor-pointer">
                <div className="inline-flex items-center gap-1 px-3 py-1.5 bg-[#cb9944] text-white text-xs rounded-full hover:bg-[#b08435] transition-colors">
                  <Upload className="w-3 h-3" />
                  {uploadingLogo ? 'Uploading...' : 'Upload Logo'}
                </div>
                <input type="file" accept="image/*" onChange={handleLogoUpload} className="hidden" disabled={uploadingLogo} />
              </label>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="edit-full-name">Full Name</Label>
              <Input
                id="edit-full-name"
                value={form.full_name || ''}
                onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                className="bg-gray-50 border-gray-200 rounded-xl"
              />
            </div>
            <div>
              <Label htmlFor="edit-display-name">Display Name</Label>
              <Input
                id="edit-display-name"
                value={form.display_name || ''}
                onChange={(e) => setForm({ ...form, display_name: e.target.value })}
                className="bg-gray-50 border-gray-200 rounded-xl"
              />
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="edit-email">Email</Label>
              <Input
                id="edit-email"
                type="email"
                value={form.email || ''}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="bg-gray-50 border-gray-200 rounded-xl"
              />
            </div>
            <div>
              <Label htmlFor="edit-phone">Phone</Label>
              <Input
                id="edit-phone"
                value={form.phone || ''}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                className="bg-gray-50 border-gray-200 rounded-xl"
              />
            </div>
          </div>
          
          <div>
            <Label htmlFor="edit-area">Area/Chapter</Label>
            <Select value={form.area || ''} onValueChange={(val) => setForm({ ...form, area: val })}>
              <SelectTrigger className="bg-gray-50 border-gray-200 rounded-xl">
                <SelectValue placeholder="Select area" />
              </SelectTrigger>
              <SelectContent>
                {areas.map(area => (
                  <SelectItem key={area} value={area}>{area}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div>
            <Label htmlFor="edit-business-name">Business Name</Label>
            <Input
              id="edit-business-name"
              value={form.business_name || ''}
              onChange={(e) => setForm({ ...form, business_name: e.target.value })}
              className="bg-gray-50 border-gray-200 rounded-xl"
            />
          </div>
          
          <div>
            <Label htmlFor="edit-business-category">Business Category</Label>
            <Select value={form.business_category || ''} onValueChange={(val) => setForm({ ...form, business_category: val })}>
              <SelectTrigger className="bg-gray-50 border-gray-200 rounded-xl">
                <SelectValue placeholder="Select category" />
              </SelectTrigger>
              <SelectContent>
                {CATEGORIES.map(cat => (
                  <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div>
            <Label htmlFor="edit-business-description">Business Description</Label>
            <Textarea
              id="edit-business-description"
              value={form.business_description || ''}
              onChange={(e) => setForm({ ...form, business_description: e.target.value })}
              className="bg-gray-50 border-gray-200 rounded-xl min-h-[80px]"
            />
          </div>
          
          <div>
            <Label htmlFor="edit-website">Website</Label>
            <Input
              id="edit-website"
              value={form.website || ''}
              onChange={(e) => setForm({ ...form, website: e.target.value })}
              className="bg-gray-50 border-gray-200 rounded-xl"
            />
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="edit-facebook">Facebook</Label>
              <Input
                id="edit-facebook"
                value={form.facebook || ''}
                onChange={(e) => setForm({ ...form, facebook: e.target.value })}
                className="bg-gray-50 border-gray-200 rounded-xl"
              />
            </div>
            <div>
              <Label htmlFor="edit-instagram">Instagram</Label>
              <Input
                id="edit-instagram"
                value={form.instagram || ''}
                onChange={(e) => setForm({ ...form, instagram: e.target.value })}
                className="bg-gray-50 border-gray-200 rounded-xl"
              />
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="edit-linkedin">LinkedIn</Label>
              <Input
                id="edit-linkedin"
                value={form.linkedin || ''}
                onChange={(e) => setForm({ ...form, linkedin: e.target.value })}
                className="bg-gray-50 border-gray-200 rounded-xl"
              />
            </div>
            <div>
              <Label htmlFor="edit-tiktok">TikTok</Label>
              <Input
                id="edit-tiktok"
                value={form.tiktok || ''}
                onChange={(e) => setForm({ ...form, tiktok: e.target.value })}
                className="bg-gray-50 border-gray-200 rounded-xl"
              />
            </div>
          </div>

          {/* Tags Section */}
          <div>
            <Label htmlFor="edit-tags">Tags (comma separated)</Label>
            <Input
              id="edit-tags"
              value={(form.tags || []).join(', ')}
              onChange={(e) => setForm({ 
                ...form, 
                tags: e.target.value.split(',').map(t => t.trim()).filter(t => t) 
              })}
              className="bg-gray-50 border-gray-200 rounded-xl"
              placeholder="e.g. Featured, Top Seller, New Member"
            />
            <p className="text-xs text-gray-500 mt-1">Add custom tags to highlight this member</p>
          </div>

          {/* Managed Chapters Section (for Chapter Leaders) */}
          <div className="border-t pt-4 mt-4">
            <Label className="text-[#5b7f95] font-semibold">Chapter Leader Settings</Label>
            <p className="text-xs text-gray-500 mb-2">Select chapters this member should lead (leave empty for regular members)</p>
            <div className="grid grid-cols-2 gap-2">
              {areas.map(area => (
                <label key={area} className="flex items-center gap-2 cursor-pointer p-2 hover:bg-gray-50 rounded-lg">
                  <input
                    type="checkbox"
                    checked={(form.managed_areas || []).includes(area)}
                    onChange={(e) => {
                      const currentAreas = form.managed_areas || [];
                      if (e.target.checked) {
                        setForm({ ...form, managed_areas: [...currentAreas, area] });
                      } else {
                        setForm({ ...form, managed_areas: currentAreas.filter(a => a !== area) });
                      }
                    }}
                    className="w-4 h-4 rounded border-gray-300 text-[#5b7f95]"
                  />
                  <span className="text-sm text-gray-700">{area}</span>
                </label>
              ))}
            </div>
          </div>
          
          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="flex-1 rounded-full"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={saving}
              className="flex-1 bg-[#5b7f95] hover:bg-[#4a6b80] text-white rounded-full"
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
